import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  ADMIN_THEME_LS_KEY,
  applyAdminThemeToHtml,
  isAdminShellLocation,
  readAdminThemePreference,
  writeAdminThemePreference,
} from "../utils/adminThemeStorage";

const AdminThemeContext = createContext({
  themeMode: "light",
  toggleTheme: () => {},
  setThemeMode: () => {},
});

/**
 * Lives under `BrowserRouter` (see `App.js`), **not** inside per-route `Layout`,
 * so it does not remount on every dashboard page and `<html data-theme>` stays
 * aligned with `themeMode` after reload.
 *
 * Off admin-shell URLs we strip `data-theme` so student/public pages keep the
 * light `:root` tokens even if the user prefers dark in the dashboard.
 */
export const AdminThemeProvider = ({ children }) => {
  const { pathname, hash } = useLocation();
  const shell = useMemo(
    () => isAdminShellLocation(pathname, hash),
    [pathname, hash]
  );

  const [themeMode, setThemeModeState] = useState(
    () => readAdminThemePreference()
  );

  useLayoutEffect(() => {
    if (!shell) {
      applyAdminThemeToHtml("light");
      return;
    }
    applyAdminThemeToHtml(themeMode);
    writeAdminThemePreference(themeMode);
  }, [shell, themeMode]);

  /**
   * Some UI libs flush styles after layout; one extra frame re-applies
   * `data-theme` so CSS variables cannot drift from React state.
   */
  useEffect(() => {
    if (!shell) return undefined;
    const id = window.requestAnimationFrame(() => {
      applyAdminThemeToHtml(themeMode);
    });
    return () => window.cancelAnimationFrame(id);
  }, [shell, themeMode]);

  /** Other tabs updating localStorage */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== ADMIN_THEME_LS_KEY || e.storageArea !== window.localStorage)
        return;
      if (!shell) return;
      const next = readAdminThemePreference();
      setThemeModeState(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [shell]);

  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode === "dark" ? "dark" : "light");
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeModeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({ themeMode, toggleTheme, setThemeMode }),
    [themeMode, toggleTheme, setThemeMode]
  );

  return (
    <AdminThemeContext.Provider value={value}>
      {children}
    </AdminThemeContext.Provider>
  );
};

export const useAdminTheme = () => useContext(AdminThemeContext);

export default AdminThemeContext;
